// AdminDashboard.js
import React, { useState } from 'react';
import { Box, Button, Typography, Grid, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [maxQuantity, setMaxQuantity] = useState(10);
  const [threshold, setThreshold] = useState(10);
  const [dryRun, setDryRun] = useState(true);

  const adminLinks = [
    { path: '/firestoreviewer', label: 'Firestore Viewer' },
    { path: '/usermanagement', label: 'User Management' },
    { path: '/notificationsmanager', label: 'Bulk Notifications' },
    { path: '/planmanagement', label: 'Subscription Plans' },
    { path: '/all-sales-summary', label: 'All Sales Summary' },
    { path: '/all-listings-summary', label: 'All Listings Summary' },
    { path: '/all-wishlist-summary', label: 'All Wishlist Summary' },
    { path: '/bulkmessage', label: 'Bulk Message Sender' },
    { path: '/addnewcard', label: 'Add New Card' },
    // Add other admin links as needed
  ];

  const handleCleanupClick = () => {
    setCleanupDialogOpen(true);
    setResult(null);
    setError(null);
  };

  const runCleanup = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const functions = getFunctions();
      const cleanupFunction = httpsCallable(functions, 'cleanupInflatedListings');
      
      const response = await cleanupFunction({
        maxQuantity: parseInt(maxQuantity),
        threshold: parseInt(threshold),
        dryRun: dryRun
      });

      setResult(response.data);
      setLoading(false);
    } catch (err) {
      // Handle timeout gracefully - function still completes in background
      if (err.code === 'deadline-exceeded' || err.message.includes('deadline-exceeded')) {
        setError('Function is taking longer than 60 seconds. It will continue running in the background and save results to Firestore. Check admin/reports/cleanupReports in Firestore Database for the results.');
        setLoading(false);
      } else {
        setError(err.message || 'An error occurred');
        setLoading(false);
      }
    }
  };

  const handleCloseDialog = () => {
    setCleanupDialogOpen(false);
    setResult(null);
    setError(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" align="center" gutterBottom>
        Admin Dashboard
      </Typography>
      
      {/* Cleanup Button - Featured */}
      <Box sx={{ mb: 4, p: 2, backgroundColor: '#fff3e0', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          🔧 Database Maintenance
        </Typography>
        <Button
          variant="contained"
          color="warning"
          size="large"
          onClick={handleCleanupClick}
          sx={{ textTransform: 'none' }}
        >
          Cleanup Inflated Listings
        </Button>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          Fix seller listings with abnormally high quantities
        </Typography>
      </Box>

      <Grid container spacing={2} justifyContent="center">
        {adminLinks.map((link) => (
          <Grid item key={link.path} xs={12} sm={6} md={4}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate(link.path)}
              sx={{ textTransform: 'none' }}
            >
              {link.label}
            </Button>
          </Grid>
        ))}
      </Grid>

      {/* Cleanup Dialog */}
      <Dialog open={cleanupDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Cleanup Inflated Listings
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Always run in DRY RUN mode first to review changes before executing!
          </Alert>

          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Threshold (flag listings above this)"
              type="number"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              sx={{ mb: 2 }}
              inputProps={{ min: 1 }}
            />

            <TextField
              fullWidth
              label="Max Quantity (cap at this value)"
              type="number"
              value={maxQuantity}
              onChange={(e) => setMaxQuantity(e.target.value)}
              sx={{ mb: 2 }}
              inputProps={{ min: 1 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Mode</InputLabel>
              <Select
                value={dryRun}
                onChange={(e) => setDryRun(e.target.value === 'true')}
                label="Mode"
              >
                <MenuItem value={true}>DRY RUN (safe - just reports)</MenuItem>
                <MenuItem value={false}>EXECUTE (actually fixes data)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Running cleanup... This may take several minutes.</Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Error: {error}
            </Alert>
          )}

          {result && result.success && (
            <Box sx={{ mt: 2 }}>
              <Alert severity={dryRun ? "info" : "success"} sx={{ mb: 2 }}>
                ✅ Cleanup completed! {dryRun ? '(DRY RUN - no changes made)' : '(DATA UPDATED)'}
              </Alert>

              <Typography variant="h6" gutterBottom>Statistics:</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="h5" color="primary">{result.report.statistics.totalListingsScanned.toLocaleString()}</Typography>
                    <Typography variant="caption">Scanned</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="h5" color="error">{result.report.statistics.listingsAboveThreshold.toLocaleString()}</Typography>
                    <Typography variant="caption">Above Threshold</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="h5" color="success">{result.report.statistics.listingsFixed.toLocaleString()}</Typography>
                    <Typography variant="caption">Fixed</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="h5">{result.report.statistics.sellerCount.toLocaleString()}</Typography>
                    <Typography variant="caption">Sellers Affected</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="h5">{result.report.statistics.quantityReduced.toLocaleString()}</Typography>
                    <Typography variant="caption">Qty Reduced</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="h5" color={result.report.statistics.errors.length > 0 ? "error" : "success"}>{result.report.statistics.errors.length}</Typography>
                    <Typography variant="caption">Errors</Typography>
                  </Box>
                </Grid>
              </Grid>

              {result.report.affectedSellers && result.report.affectedSellers.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Affected Sellers: {result.report.affectedSellers.join(', ')}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          <Button 
            onClick={runCleanup} 
            variant="contained" 
            color={dryRun ? "primary" : "error"}
            disabled={loading}
          >
            {loading ? 'Running...' : dryRun ? 'Run Dry Run' : 'Execute Cleanup'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
